import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';

import { AuthContext, AuthToken, ServiceAccessTokenProvider, ServiceTokenRequestDto } from '@celum/authentication';
import { PortalConfiguration, PortalsProperties } from '@celum/portals/domain';
import { createCloudTokenApiConfigForPortals, PORTAL_CLOUD_TOKEN_QUERY_PARAM_NAME } from '@celum/portals/shared';

/**
 * HTTP interceptor for the Portals app that extracts the `cloudToken` from the configuration API response.
 *
 * The `cloudToken` is captured from the configuration endpoint response and stored in the local token cache (local storage).
 *
 * As a sidenote, this also ensures that the `cloudToken` is already available when the backend accesses the portal in a headless browser,
 * using the `portalReadToken` query parameter, to generate a preview.
 */
@Injectable()
export class PortalsCloudTokenInterceptor implements HttpInterceptor {
  private readonly portalConfigUrl = `${PortalsProperties.properties.apiUrl}/configuration/`;

  private serviceAccessTokenProvider = inject(ServiceAccessTokenProvider);

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only retrieve cloud tokens from requests to the portal configuration API
    if (!req.url.startsWith(this.portalConfigUrl)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          const portalConfig = event.body as PortalConfiguration;

          const { id, organizationId, cloudToken } = portalConfig;

          if (!cloudToken) {
            return;
          }

          this.putCloudTokenToCache(id, organizationId, cloudToken);
        }
      })
    );
  }

  private putCloudTokenToCache(portalId: string, organizationId: string, cloudToken: AuthToken): void {
    // we recreate the api configuration here as at the point of interception the api config does not yet exist
    const apiConfiguration = createCloudTokenApiConfigForPortals(organizationId);

    // let's find the token endpoint config for the cloud token
    const tokenEndpointConfig = apiConfiguration.tokenEndpointConfigs.find(config => config.tokenQueryParamName === PORTAL_CLOUD_TOKEN_QUERY_PARAM_NAME);

    if (!tokenEndpointConfig) {
      return;
    }

    const authContext: AuthContext = { portalId };

    this.serviceAccessTokenProvider.putTokenToCache(tokenEndpointConfig.serviceTokenRequestDto as ServiceTokenRequestDto, authContext, cloudToken);
  }
}
